import Box from '@components/base/Box'
import { BaseInputProps } from './BaseInputProps'
import { validate } from './Validations'
import { v4 } from 'uuid'
import { useEffect, useRef, useState } from 'react'
import Tooltip from '@components/tootilp/Tooltip'
import { MdKeyboardArrowDown } from 'react-icons/md'

export type DropdownOptions = {
  label: string
  value: string | number
  disabled?: boolean
  show?: boolean
}

export interface DropdownInputProps extends BaseInputProps {
  /**
   * The type of the input.
   */
  onChange?:
    | ((
        value: any,
        isInvalid?: boolean | undefined,
        index?: number | undefined
      ) => void)
    | undefined
  options: DropdownOptions[] | string[]
  textColour?: string
  // The value of the selected option.
  selected?: any
  labelBgColour?: string
  bgColor?: string
  noBorder?: boolean
  autoWidth?: boolean
  maxWidth?: string
  id?: string
  overrideFullLabelClass?: string
  selectClass?: string
}

const getOptionLabel = (option: DropdownOptions | string) =>
  typeof option === 'string' ? option : option.label

const getOptionValue = (option: DropdownOptions | string) =>
  typeof option === 'string' ? option : String(option.value)

const isOptionDisabled = (
  option: DropdownOptions | string,
  disabled: boolean
) => (typeof option === 'string' ? disabled : !!option.disabled)

/**
 * Primary UI component for dropdown input
 */
export const DropdownInput = ({
  label,
  placeholder,
  value = '',
  onChange,
  className,
  hint,
  validations,
  required,
  options,
  disabled = false,
  selected,
  noBorder = false,
  autoWidth = false,
  maxWidth = 'inherit',
  id,
  tooltip,
  bgColor = 'bg-white/70',
  overrideFullLabelClass = undefined,
  selectClass = '',
  error,
  show = true,
  textColour,
  disableInlineErrors = false,
}: DropdownInputProps) => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLLabelElement>(null)
  const dropDownId = useRef(id ?? v4()).current
  const currentValue = selected != null ? String(selected) : String(value ?? '')
  const normalizedOptions: Array<DropdownOptions | string> = options ?? []
  const selectedOption = normalizedOptions.find(
    (option) => getOptionValue(option) === currentValue
  )
  const displayValue = selectedOption
    ? getOptionLabel(selectedOption)
    : placeholder || 'Select an option'
  const errorMessage = error ?? validate(value, validations)

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  if (!show) return <></>

  const mainLabelclassName = overrideFullLabelClass
    ? overrideFullLabelClass
    : ['relative flex flex-col my-3 text-sm', className].join(' ')

  const controlClassName = [
    'flex min-h-11 items-center justify-between gap-3 rounded border px-3 py-2 text-left text-sm outline-none transition-all focus:ring-0',
    autoWidth ? 'w-fit min-w-[80px]' : 'w-full',
    bgColor,
    noBorder ? 'border-transparent' : '',
    errorMessage
      ? 'border-red-500 text-red-500 focus:border-red-500'
      : 'border-gray-300 text-gray-900 hover:border-forumm-blue focus:border-forumm-blue',
    disabled ? 'cursor-not-allowed !bg-gray-100 !text-gray-500' : 'cursor-pointer',
    selectClass,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <label
      ref={dropdownRef}
      className={mainLabelclassName}
      style={{ color: textColour, maxWidth }}
    >
      {label && (
        <span
          className={[
            'mb-1.5 text-sm font-medium leading-5 transition-all',
            errorMessage ? 'text-red-500' : '',
          ].join(' ')}
        >
          <Tooltip tooltip={tooltip} className="flex flex-col">
            <span
              className={[
                'z-10',
                required
                  ? "after:content-['*'] after:ml-1 after:text-red-500"
                  : '',
              ].join(' ')}
            >
              {label}
            </span>
            {hint ? <span className="ml-4 text-xs">{hint}</span> : null}
          </Tooltip>
        </span>
      )}
      <Box className="relative w-full" ignoreTheme>
        <button
          id={dropDownId}
          data-testid={id ?? undefined}
          type="button"
          role="combobox"
          aria-controls={dropDownId + '-listbox'}
          aria-expanded={open}
          aria-label={label || placeholder || 'Select an option'}
          aria-required={required}
          disabled={disabled}
          onClick={() => setOpen((state) => !state)}
          className={controlClassName}
        >
          <span
            className={[
              'min-w-0 flex-1 whitespace-normal break-words leading-snug',
              selectedOption ? '' : 'text-gray-500',
            ].join(' ')}
          >
            {displayValue}
          </span>
          <MdKeyboardArrowDown
            className={[
              'h-5 w-5 shrink-0 text-gray-500 transition-transform',
              open ? 'rotate-180' : '',
            ].join(' ')}
          />
        </button>
        <Box
          id={dropDownId + '-listbox'}
          role="listbox"
          show={open}
          className="absolute left-0 right-0 top-full z-50 mt-1 flex max-h-64 max-w-full flex-col gap-1 overflow-y-auto rounded border border-gray-300 bg-white p-1 text-gray-900 shadow-xl"
          ignoreTheme
        >
          {normalizedOptions.map((option, optionIndex) => {
            const optionValue = getOptionValue(option)
            const optionLabel = getOptionLabel(option)
            const optionDisabled = isOptionDisabled(option, disabled)
            const isSelected = optionValue === currentValue
            const selectedIndex = optionIndex + (placeholder ? 1 : 0)

            return (
              <button
                key={optionValue + '::' + optionLabel}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={optionDisabled}
                onClick={() => {
                  const isInvalid = validate(optionValue, validations) !== ''
                  onChange?.(optionValue, isInvalid, selectedIndex)
                  setOpen(false)
                }}
                className={[
                  'w-full rounded px-3 py-2 text-left text-sm leading-snug transition-all',
                  isSelected
                    ? 'bg-forumm-blue/10 text-forumm-blue'
                    : 'text-gray-900 hover:bg-gray-50 hover:text-forumm-blue',
                  optionDisabled
                    ? 'cursor-not-allowed text-gray-400 hover:bg-white hover:text-gray-400'
                    : 'cursor-pointer',
                ].join(' ')}
              >
                <span className="block whitespace-normal break-words">
                  {optionLabel}
                </span>
              </button>
            )
          })}
        </Box>
      </Box>
      {errorMessage && !disableInlineErrors && (
        <Box ignoreTheme className="mt-1 text-sm font-bold text-red-500">
          {errorMessage}
        </Box>
      )}
    </label>
  )
}
